import { CouponPacksQuery, useCouponPacksLazyQuery } from '../../../../graphql/generated';
import { useCallback, useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { SearchFormValues } from '../components';
import { useSnackbar } from 'notistack';

const PageItemsCount = 25;

export type ListCouponPackIssues = {
  loading: boolean;
  onSearchCouponPacks: (condition: SearchFormValues) => void;
  data?: CouponPacksQuery;
  onFetchMore: () => void;
};

export const useCouponPacks = (couponPackMasterId: string): ListCouponPackIssues => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [listQuery, queryContext] = useCouponPacksLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
      enqueueSnackbar('サーバーエラーが発生した', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      Bugsnag.notify(error);
    },
  });

  // 初回、検索条件なしで表示する
  useEffect(() => {
    setLoading(true);
    listQuery({
      variables: {
        couponPackMasterId,
        limit: PageItemsCount,
      },
    });
  }, [listQuery]);

  // 動的な検索条件をセットしてクーポンマスタを検索する
  const onSearchCouponPacks = useCallback(
    (data: SearchFormValues) => {
      setLoading(true);
      listQuery({
        variables: {
          couponPackMasterId,
          [data.searchCondition]: data.value,
          limit: PageItemsCount,
        },
      });
    },
    [listQuery, couponPackMasterId]
  );

  // さらに読み込む
  const onFetchMore = useCallback(async () => {
    if (queryContext?.fetchMore && queryContext.variables) {
      setLoading(true);
      await queryContext.fetchMore({
        variables: {
          couponPackMasterId,
          couponPackUrl: queryContext.variables.couponPackUrl,
          nextToken: queryContext.data?.couponPacks.pageInfo.nextToken,
          limit: PageItemsCount,
          organizationId: queryContext.variables.organizationId,
          serialNo: queryContext.variables.serialNo,
        },
        updateQuery: (prevQuery, { fetchMoreResult }) => {
          setLoading(false);
          if (!fetchMoreResult) {
            return prevQuery;
          }
          return {
            couponPacks: {
              __typename: fetchMoreResult.couponPacks.__typename,
              nodes: [...prevQuery.couponPacks.nodes, ...fetchMoreResult.couponPacks.nodes],
              pageInfo: fetchMoreResult.couponPacks.pageInfo,
            },
          };
        },
      });
    }
  }, [queryContext]);

  return {
    loading,
    onSearchCouponPacks,
    data: queryContext.data,
    onFetchMore,
  };
};
